import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import api from '../lib/axios';
import { dateFmt } from '../lib/format';

type AiOperation =
  | 'STATEMENT_IMPORT'
  | 'STATEMENT_PDF_PARSE'
  | 'ACCOUNT_SUGGESTION'
  | 'ACCOUNT_CLASSIFICATION'
  | 'DOCUMENT_SCAN'
  | 'TELEGRAM_COLLECTION'
  | 'RECONCILIATION_ANALYSIS';

interface OperationCost {
  operation: AiOperation;
  calls: number;
  costUsd: string;
  inputTokens: number;
  outputTokens: number;
}

interface UsageRow {
  id: number;
  operation: AiOperation;
  model: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  costUsd: string;
  reference: string | null;
  createdAt: string;
}

interface Summary {
  totalUsd: string;
  totalCalls: number;
  byOperation: OperationCost[];
  recent: UsageRow[];
}

const usd = (v: number | string): string =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 4 }).format(
    typeof v === 'string' ? Number.parseFloat(v) || 0 : v
  );

const num = (v: number): string => new Intl.NumberFormat('fr-FR').format(v);

export default function AiCosts() {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['ai-usage-summary'],
    queryFn: () => api.get<Summary>('/ai-usage/summary').then((r) => r.data),
    refetchInterval: 60_000,
  });

  const opLabel = (op: AiOperation): string => t(`aiCosts.operations.${op}`);

  return (
    <div>
      <div className="mb-6">
        <p className="eyebrow">{t('nav.aiCosts')}</p>
        <h1 className="page-title mt-1">{t('aiCosts.title')}</h1>
        <p className="mt-1 text-sm text-ink-muted">{t('aiCosts.intro')}</p>
      </div>

      <ModelConfigCard />

      {isLoading && <p className="text-ink-muted">{t('common.loading')}</p>}

      {data && (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="card p-5">
              <div className="field-label flex items-center gap-2">
                <Sparkles size={16} className="text-brand" /> {t('aiCosts.total')}
              </div>
              <div className="mt-2 font-display text-3xl font-semibold text-ink tnum">
                {usd(data.totalUsd)}
              </div>
            </div>
            <div className="card p-5">
              <div className="field-label">{t('aiCosts.calls')}</div>
              <div className="mt-2 font-display text-3xl font-semibold text-ink tnum">
                {num(data.totalCalls)}
              </div>
            </div>
          </div>

          <div className="mb-6 card overflow-hidden">
            <div className="border-b border-line px-5 py-3 font-medium text-ink">
              {t('aiCosts.byOperation')}
            </div>
            {data.byOperation.length === 0 ? (
              <div className="p-8 text-center text-ink-muted">{t('aiCosts.empty')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('aiCosts.operation')}</th>
                      <th className="text-right">{t('aiCosts.calls')}</th>
                      <th className="text-right">{t('aiCosts.inputTokens')}</th>
                      <th className="text-right">{t('aiCosts.outputTokens')}</th>
                      <th className="text-right">{t('aiCosts.cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byOperation.map((row) => (
                      <tr key={row.operation}>
                        <td data-label={t('aiCosts.operation')} className="font-medium text-ink">
                          {opLabel(row.operation)}
                        </td>
                        <td data-label={t('aiCosts.calls')} className="text-right tnum">{num(row.calls)}</td>
                        <td data-label={t('aiCosts.inputTokens')} className="text-right tnum">{num(row.inputTokens)}</td>
                        <td data-label={t('aiCosts.outputTokens')} className="text-right tnum">{num(row.outputTokens)}</td>
                        <td data-label={t('aiCosts.cost')} className="text-right tnum font-medium text-ink">{usd(row.costUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card overflow-hidden">
            <div className="border-b border-line px-5 py-3 font-medium text-ink">
              {t('aiCosts.recent')}
            </div>
            {data.recent.length === 0 ? (
              <div className="p-8 text-center text-ink-muted">{t('aiCosts.empty')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('aiCosts.date')}</th>
                      <th>{t('aiCosts.operation')}</th>
                      <th>{t('aiCosts.model')}</th>
                      <th>{t('aiCosts.reference')}</th>
                      <th className="text-right">{t('aiCosts.tokens')}</th>
                      <th className="text-right">{t('aiCosts.cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((row) => (
                      <tr key={row.id}>
                        <td data-label={t('aiCosts.date')} className="text-ink-soft">{dateFmt(row.createdAt)}</td>
                        <td data-label={t('aiCosts.operation')} className="font-medium text-ink">
                          {opLabel(row.operation)}
                        </td>
                        <td data-label={t('aiCosts.model')} className="text-ink-soft">{row.model ?? '-'}</td>
                        <td data-label={t('aiCosts.reference')} className="text-ink-soft">{row.reference ?? '-'}</td>
                        <td data-label={t('aiCosts.tokens')} className="text-right tnum text-ink-soft">
                          {num(row.inputTokens + row.outputTokens)}
                        </td>
                        <td data-label={t('aiCosts.cost')} className="text-right tnum font-medium text-ink">{usd(row.costUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface ModelOption {
  id: string;
  label: string;
  description: string;
  recommended: boolean;
}

interface ModelConfig {
  current: string;
  defaultModel: string;
  available: ModelOption[];
}

function ModelConfigCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [custom, setCustom] = useState('');
  const [saved, setSaved] = useState(false);

  const { data } = useQuery({
    queryKey: ['ai-model-config'],
    queryFn: () => api.get<ModelConfig>('/admin/ai-model').then((r) => r.data),
  });

  const isKnown = (id: string) => (data?.available ?? []).some((m) => m.id === id);
  const current = data?.current ?? '';
  const chosen = selected ?? current;
  const useCustom = chosen === '__custom__' || (!!current && !isKnown(current) && selected === null);
  const effectiveModel = useCustom ? custom || (isKnown(current) ? '' : current) : chosen;

  const save = useMutation({
    mutationFn: (model: string) =>
      api.put<ModelConfig>('/admin/ai-model', { model }).then((r) => r.data),
    onSuccess: (res) => {
      qc.setQueryData(['ai-model-config'], res);
      setSelected(null);
      setCustom('');
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  if (!data) return null;

  return (
    <div className="card mb-6 p-5">
      <div className="field-label flex items-center gap-2">
        <Sparkles size={16} className="text-brand" /> {t('aiCosts.modelTitle')}
      </div>
      <p className="mt-1 text-sm text-ink-muted">{t('aiCosts.modelIntro')}</p>

      <div className="mt-4 space-y-2">
        {data.available.map((m) => {
          const active = (useCustom ? '__custom__' : chosen) === m.id;
          return (
            <label
              key={m.id}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                active ? 'border-brand bg-brand-tint/40' : 'border-line hover:border-brand/40'
              }`}
            >
              <input
                type="radio"
                name="ai-model"
                className="mt-1"
                checked={active}
                onChange={() => setSelected(m.id)}
              />
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink">{m.label}</span>
                  <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs text-ink-soft tnum">
                    {m.id}
                  </span>
                  {m.recommended && (
                    <span className="rounded bg-income/15 px-1.5 py-0.5 text-xs font-medium text-income">
                      {t('aiCosts.modelRecommended')}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block text-sm text-ink-muted">{m.description}</span>
              </span>
            </label>
          );
        })}

        <label
          className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${
            useCustom ? 'border-brand bg-brand-tint/40' : 'border-line hover:border-brand/40'
          }`}
        >
          <input
            type="radio"
            name="ai-model"
            checked={useCustom}
            onChange={() => setSelected('__custom__')}
          />
          <span className="flex-1">
            <span className="font-medium text-ink">{t('aiCosts.modelCustom')}</span>
            <input
              className="input mt-2 w-full tnum"
              placeholder={t('aiCosts.modelCustomPlaceholder')}
              value={useCustom ? custom || (isKnown(current) ? '' : current) : ''}
              onFocus={() => setSelected('__custom__')}
              onChange={(e) => setCustom(e.target.value)}
            />
          </span>
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          className="btn btn-primary"
          disabled={save.isPending || !effectiveModel || effectiveModel === current}
          onClick={() => effectiveModel && save.mutate(effectiveModel)}
        >
          {t('common.save')}
        </button>
        {saved && <span className="text-sm font-medium text-income">{t('settings.saved')}</span>}
        <span className="ml-auto text-xs text-ink-muted">
          {t('aiCosts.modelCurrent')} <span className="tnum text-ink-soft">{current}</span>
        </span>
      </div>
    </div>
  );
}

